/**
 * section-tools.tsx — Phần "Công cụ" của mục "Kỹ năng & công cụ" (Cần phát hành):
 *   - Danh sách công cụ ĐÃ GẮN (tools-lib.ts suy từ toolsJson + settings): tên, nền tảng (logo), trạng thái
 *     (Đang dùng / Chưa uỷ quyền / Mất kết nối / Đang tắt), công tắc, nút gỡ.
 *   - "Thêm công cụ" → popup 2 thẻ: NỀN TẢNG (ứng dụng đã kết nối ở Tích hợp — Evotech đủ công cụ; app khác "Sắp có";
 *     trạng thái Thêm kết nối → Uỷ quyền → Thêm) và TUỲ CHỈNH (công cụ sẵn có của eCDP; HTTP tuỳ chỉnh "Sắp có").
 *   - Uỷ quyền: hộp liệt kê dữ liệu AI được đọc (chỉ đọc) → "Đồng ý uỷ quyền" (POST /integrations/evotech/ai-authorize).
 */
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { i18n } from '@/lib/i18n'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ArrowRight, Check, ExternalLink, Globe, Loader2, Plus, RefreshCw, ShieldCheck, Trash2, Wrench } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch, Checkbox } from '@/components/ui/misc'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import { formatVnd } from '@/lib/format-money'
import { cn } from '@/lib/utils'
import {
  aiKeys, useAiDataSources, usePendingOrders, cancelPendingOrder, type AiBotToolsJson, type AiDataSource,
} from '@/hooks/use-ai'
import { useAuthorizeEvotechAi, useAuthorizeEvotechAiOrder } from '@/hooks/use-evotech'
import { BRAND_LOGOS, type BrandKey } from '@/lib/brand-logos'
import { StaffPanel } from './staff-ui'
import { StatusBadge } from '../train/settings-row'
import { toggleSource } from '../data-sources-lib'
import {
  attachedTools, platformToolAttached, setPlatformTool, setOrderConfig, TOOL_STATUS_LABEL, type AttachedTool, type ToolStatus,
} from '../train/tools-lib'
import { toolCatalogKey, useToolCatalog, type PlatformEntry } from '../train/train-api'
import { patchTool, type SectionProps } from '../train/draft-form'

function PlatformIcon({ id, className }: { id: string; className?: string }) {
  const logo = (BRAND_LOGOS as Record<string, { src: string }>)[id as BrandKey]
  if (logo) return <img src={logo.src} alt="" className={cn('h-5 w-5 shrink-0 rounded object-contain', className)} />
  return <span className={cn('flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/10 text-primary', className)}><Wrench className="h-3 w-3" /></span>
}

const STATUS_VARIANT: Record<ToolStatus, 'success' | 'warning' | 'destructive' | 'secondary'> = { active: 'success', unauthorized: 'warning', disconnected: 'destructive', off: 'secondary' }

export function ToolsSection(props: SectionProps & { onOpenSection?: (id: 'chuyen-nguoi' | 'nang-cao') => void }) {
  const { t } = useTranslation('aiStaff')
  const { botId, form, set, setSettings, canEdit, onOpenSection } = props
  const { data: dsData } = useAiDataSources()
  const sources = dsData?.sources ?? []
  const tools = form.toolsJson
  const [addOpen, setAddOpen] = useState(false)
  const list = attachedTools(tools, form.settings, sources)

  const toggle = (tl: AttachedTool, on: boolean) => {
    if (tl.id === 'ecdp.knowledge') return set({ toolsJson: patchTool(tools, 'search_knowledge', { enabled: on }) })
    if (tl.id === 'ecdp.appointment') return setSettings((x) => ({ ...x, appointmentEnabled: on }))
    if (tl.id === 'ecdp.products' || tl.id.endsWith('.products')) return set({ toolsJson: patchTool(tools, 'search_products', { enabled: on }) })
    if (tl.id.endsWith('.stock') || tl.id.endsWith('.orders') || tl.id.endsWith('.member') || tl.id.endsWith('.order_create')) {
      return set({ toolsJson: setPlatformTool(tl.id, on, tools, sources) })
    }
  }
  const remove = (tl: AttachedTool) => {
    if (tl.id === 'ecdp.products') {
      let next: AiBotToolsJson = { ...(tools ?? {}) }
      for (const k of ['import', 'manual']) next = toggleSource(next, sources, 'products', k, false)
      return set({ toolsJson: next })
    }
    set({ toolsJson: setPlatformTool(tl.id, false, tools, sources) })
  }
  const open = (tl: AttachedTool) => {
    if (tl.id === 'ecdp.handoff') onOpenSection?.('chuyen-nguoi')
    else if (tl.id === 'ecdp.autostop') onOpenSection?.('nang-cao')
  }

  return (
    <StaffPanel
      id="staff-cong-cu" title={t('tools.title')} icon={<Wrench className="h-4 w-4" />} scope="publish"
      description={t('tools.description')}
      actions={<Button size="sm" variant="outline" onClick={() => setAddOpen(true)} disabled={!canEdit}><Plus className="h-3.5 w-3.5" /> {t('tools.add')}</Button>}
    >
      <ul className="divide-y rounded-lg border">
        {list.map((tl) => (
          <li key={tl.id} className="group flex min-h-[56px] items-center gap-3 px-3 py-2 hover:bg-muted/40">
            <PlatformIcon id={tl.platform} />
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-2 text-[15px]">
                <span className="truncate">{tl.name}</span>
                <span className="shrink-0 text-[11px] text-muted-foreground">{tl.platform === 'ecdp' ? 'eCDP' : tl.platform === 'evotech' ? 'Evotech' : tl.platform}</span>
              </p>
              <p className="truncate text-xs text-muted-foreground">{tl.description}</p>
            </div>
            <Badge variant={STATUS_VARIANT[tl.status]} className="shrink-0 text-[11px]">{TOOL_STATUS_LABEL[tl.status]}</Badge>
            {tl.toggleable ? (
              <Switch checked={tl.enabled} onCheckedChange={(v) => toggle(tl, v)} disabled={!canEdit} aria-label={t('tools.enableAria', { name: tl.name })} />
            ) : (
              <button type="button" className="inline-flex items-center gap-1 text-xs text-primary hover:underline" onClick={() => open(tl)}>
                {t('common:actions.settings')} <ArrowRight className="h-3 w-3" />
              </button>
            )}
            <Button size="icon" variant="ghost" className={cn('h-7 w-7', !tl.removable && 'invisible')} title={t('tools.remove')} aria-label={t('tools.removeAria', { name: tl.name })} disabled={!canEdit} onClick={() => remove(tl)}>
              <Trash2 className="h-3.5 w-3.5 text-destructive" />
            </Button>
          </li>
        ))}
        {list.some((tl) => tl.id === 'evotech.order_create' && tl.enabled) && (
          <li className="space-y-3 bg-muted/20 px-3 py-3">
            <OrderCreateConfig tools={tools} canEdit={canEdit} onChange={(next) => set({ toolsJson: next })} />
            <OrderDraftsList botId={botId} canEdit={canEdit} />
          </li>
        )}
      </ul>
      {addOpen && <AddToolDialog onOpenSection={onOpenSection} tools={tools} sources={sources} settings={form.settings} canEdit={canEdit} onClose={() => setAddOpen(false)}
        onTools={(next) => set({ toolsJson: next })} onSettings={setSettings} />}
    </StaffPanel>
  )
}

/** Cấu hình riêng của "Tạo đơn nháp cho khách": trần giá trị, phương thức thanh toán AI được dùng, hạn nháp (phút). */
function OrderCreateConfig({ tools, canEdit, onChange }: { tools: AiBotToolsJson | null; canEdit: boolean; onChange: (next: AiBotToolsJson) => void }) {
  const { t } = useTranslation('aiStaff')
  const payTypes = tools?.orderPayTypes ?? ['COD']
  const togglePay = (v: 'COD' | 'Transfer', on: boolean) => {
    const next = on ? [...new Set([...payTypes, v])] : payTypes.filter((x) => x !== v)
    onChange(setOrderConfig(tools, { orderPayTypes: next.length ? next : ['COD'] }))
  }
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <label className="space-y-1">
        <span className="text-xs text-muted-foreground">{t('tools.orderCreate.maxValue')}</span>
        <Input
          type="number" min={1} step={1000} disabled={!canEdit}
          value={tools?.orderMaxValue ?? 5_000_000}
          onChange={(e) => {
            const v = Number(e.target.value)
            if (Number.isFinite(v) && v > 0) onChange(setOrderConfig(tools, { orderMaxValue: v }))
          }}
        />
      </label>
      <div className="space-y-1">
        <span className="text-xs text-muted-foreground">{t('tools.orderCreate.payTypes')}</span>
        <div className="flex items-center gap-4 pt-1.5">
          <label className="flex items-center gap-1.5 text-sm">
            <Checkbox checked={payTypes.includes('COD')} disabled={!canEdit} onCheckedChange={(v) => togglePay('COD', v === true)} /> COD
          </label>
          <label className="flex items-center gap-1.5 text-sm">
            <Checkbox checked={payTypes.includes('Transfer')} disabled={!canEdit} onCheckedChange={(v) => togglePay('Transfer', v === true)} /> {t('tools.orderCreate.transfer')}
          </label>
        </div>
      </div>
      <label className="space-y-1">
        <span className="text-xs text-muted-foreground">{t('tools.orderCreate.ttl')}</span>
        <Input
          type="number" min={5} max={1440} step={5} disabled={!canEdit}
          value={tools?.orderDraftTtlMinutes ?? 60}
          onChange={(e) => {
            const v = Number(e.target.value)
            if (Number.isInteger(v) && v >= 5 && v <= 1440) onChange(setOrderConfig(tools, { orderDraftTtlMinutes: v }))
          }}
        />
      </label>
    </div>
  )
}

/** "Đơn nháp do AI tạo" — của riêng bot này, đang chờ khách xác nhận. Huỷ tay dùng cùng đường huỷ với job dọn hạn. */
function OrderDraftsList({ botId, canEdit }: { botId: string; canEdit: boolean }) {
  const { t } = useTranslation('aiStaff')
  const qc = useQueryClient()
  const { data, isLoading } = usePendingOrders()
  const rows = (data?.data ?? []).filter((r) => r.payload?.botId === botId)
  const [cancelingId, setCancelingId] = useState<string | null>(null)

  const cancel = async (id: string) => {
    setCancelingId(id)
    try {
      await cancelPendingOrder(id)
      toast.success(t('tools.orderCreate.drafts.cancelled'))
      qc.invalidateQueries({ queryKey: aiKeys.pendingOrders })
    } catch (e) {
      toast.error(apiError(e))
    } finally {
      setCancelingId(null)
    }
  }

  if (isLoading) return <p className="text-xs text-muted-foreground">{t('tools.orderCreate.drafts.loading')}</p>
  if (rows.length === 0) return <p className="text-xs text-muted-foreground">{t('tools.orderCreate.drafts.empty')}</p>

  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-muted-foreground">{t('tools.orderCreate.drafts.title', { count: rows.length })}</p>
      <ul className="divide-y rounded-lg border bg-background">
        {rows.map((r) => (
          <li key={r.id} className="flex items-center gap-3 px-3 py-2 text-sm">
            <div className="min-w-0 flex-1">
              <p className="truncate">#{r.payload.orderNo} — {formatVnd(r.payload.total)}</p>
              <p className="truncate text-xs text-muted-foreground">{r.summary}</p>
            </div>
            <Button size="sm" variant="ghost" className="text-destructive" disabled={!canEdit || cancelingId === r.id} onClick={() => void cancel(r.id)}>
              {cancelingId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} {t('tools.orderCreate.drafts.cancel')}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ── Popup "Thêm công cụ" ──────────────────────────────────────────────────────

function AddToolDialog({ onOpenSection, tools, sources, settings, canEdit, onClose, onTools, onSettings }: {
  onOpenSection?: (id: 'chuyen-nguoi' | 'nang-cao') => void
  tools: AiBotToolsJson | null
  sources: AiDataSource[]
  settings: SectionProps['form']['settings']
  canEdit: boolean
  onClose: () => void
  onTools: (next: AiBotToolsJson) => void
  onSettings: SectionProps['setSettings']
}) {
  const { t } = useTranslation('aiStaff')
  const qc = useQueryClient()
  const catalog = useToolCatalog(true)
  const [authFor, setAuthFor] = useState<PlatformEntry | null>(null)
  const [authOrderFor, setAuthOrderFor] = useState<PlatformEntry | null>(null)
  const refresh = async () => {
    await Promise.all([qc.invalidateQueries({ queryKey: toolCatalogKey }), qc.invalidateQueries({ queryKey: aiKeys.dataSources })])
    toast.success(t('tools.dialog.refreshed'))
  }
  const platforms = catalog.data?.platforms ?? []

  const custom: Array<{ id: string; name: string; description: string; on: boolean; onChange?: (v: boolean) => void; action?: () => void; soon?: boolean }> = [
    { id: 'knowledge', name: t('tools.custom.knowledge.name'), description: t('tools.custom.knowledge.description'), on: tools?.search_knowledge?.enabled ?? true, onChange: (v) => onTools(patchTool(tools, 'search_knowledge', { enabled: v })) },
    { id: 'products', name: t('tools.custom.products.name'), description: t('tools.custom.products.description'), on: ['import', 'manual'].some((k) => platformToolAttached(`${k}.products`, tools, sources)),
      onChange: (v) => { let next: AiBotToolsJson = { ...(tools ?? {}) }; for (const k of ['import', 'manual']) if (sources.some((s) => s.key === k && s.products)) next = toggleSource(next, sources, 'products', k, v); onTools(next) } },
    { id: 'orders', name: t('tools.custom.orders.name'), description: t('tools.custom.orders.description'), on: platformToolAttached('api.orders', tools, sources), onChange: (v) => onTools(toggleSource(tools, sources, 'orders', 'api', v)) },
    { id: 'appointment', name: t('tools.custom.appointment.name'), description: t('tools.custom.appointment.description'), on: settings.appointmentEnabled, onChange: (v) => onSettings((x) => ({ ...x, appointmentEnabled: v })) },
    { id: 'handoff', name: t('tools.custom.handoff.name'), description: t('tools.custom.handoff.description'), on: settings.handoff.enabled, action: () => { onClose(); onOpenSection?.('chuyen-nguoi') } },
    { id: 'autostop', name: t('tools.custom.autostop.name'), description: t('tools.custom.autostop.description'), on: settings.autoStop.enabled, action: () => { onClose(); onOpenSection?.('nang-cao') } },
    { id: 'http', name: t('tools.custom.http.name'), description: t('tools.custom.http.description'), on: false, soon: true },
  ]

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wrench className="h-4 w-4 text-primary" /> {t('tools.add')}</DialogTitle>
          <DialogDescription>{t('tools.dialog.description')}</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="platform">
          <TabsList className="w-full">
            <TabsTrigger value="platform" className="flex-1">{t('tools.dialog.tabPlatform')}</TabsTrigger>
            <TabsTrigger value="custom" className="flex-1">{t('tools.dialog.tabCustom')}</TabsTrigger>
          </TabsList>
          <TabsContent value="platform" className="space-y-3 pt-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">{t('tools.dialog.platformHint')}</p>
              <Button size="sm" variant="ghost" onClick={() => void refresh()} disabled={catalog.isFetching}><RefreshCw className={cn('h-3.5 w-3.5', catalog.isFetching && 'animate-spin')} /> {t('common:actions.refresh')}</Button>
            </div>
            {catalog.isLoading && <p className="flex items-center gap-2 py-4 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('tools.dialog.loading')}</p>}
            {platforms.map((p) => (
              <PlatformBlock key={p.id} p={p} tools={tools} sources={sources} canEdit={canEdit} onTools={onTools} onAuthorize={() => setAuthFor(p)} onAuthorizeOrder={() => setAuthOrderFor(p)} />
            ))}
          </TabsContent>
          <TabsContent value="custom" className="pt-3">
            <ul className="divide-y rounded-lg border">
              {custom.map((c) => (
                <li key={c.id} className={cn('flex min-h-[52px] items-center gap-3 px-3 py-2', c.soon && 'opacity-60')}>
                  <PlatformIcon id="ecdp" />
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 text-sm font-medium">{c.name}{c.soon && <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-normal text-muted-foreground">{t('tools.soon')}</span>}</p>
                    <p className="truncate text-xs text-muted-foreground">{c.description}</p>
                  </div>
                  {c.soon ? null : c.onChange ? (
                    <Switch checked={c.on} onCheckedChange={c.onChange} disabled={!canEdit} aria-label={c.name} />
                  ) : (
                    <Button size="sm" variant="outline" onClick={c.action}><StatusBadge on={c.on} /> {t('common:actions.settings')}</Button>
                  )}
                </li>
              ))}
            </ul>
          </TabsContent>
        </Tabs>
        <div className="flex justify-end"><Button variant="ghost" onClick={onClose}>{t('common:actions.close')}</Button></div>
        {authFor && <AuthorizeDialog p={authFor} onClose={() => setAuthFor(null)} />}
        {authOrderFor && <AuthorizeOrderDialog p={authOrderFor} onClose={() => setAuthOrderFor(null)} />}
      </DialogContent>
    </Dialog>
  )
}

function PlatformBlock({ p, tools, sources, canEdit, onTools, onAuthorize, onAuthorizeOrder }: {
  p: PlatformEntry
  tools: AiBotToolsJson | null
  sources: AiDataSource[]
  canEdit: boolean
  onTools: (next: AiBotToolsJson) => void
  onAuthorize: () => void
  onAuthorizeOrder: () => void
}) {
  const { t } = useTranslation('aiStaff')
  const ready = p.connected && p.enabled && p.aiAuthorized
  let state: ReactNode
  if (p.comingSoon) state = <Badge variant="secondary">{t('tools.soon')}</Badge>
  else if (!p.connected || !p.enabled) {
    state = (
      <Button size="sm" variant="outline" onClick={() => window.open(p.settingsPath, '_blank', 'noopener')}>
        <ExternalLink className="h-3.5 w-3.5" /> {p.connected ? t('tools.platform.reenable') : t('tools.platform.addConnection')}
      </Button>
    )
  } else if (!p.aiAuthorized) {
    state = <Button size="sm" onClick={onAuthorize} disabled={!canEdit}><ShieldCheck className="h-3.5 w-3.5" /> {t('tools.platform.authorize')}</Button>
  } else state = <Badge variant="success" className="gap-1"><Check className="h-3 w-3" /> {t('tools.platform.ready')}</Badge>

  // order_create cần uỷ quyền RIÊNG (aiOrderAuthorized) — không gộp vào "Thêm tất cả" nếu chưa uỷ quyền việc đó.
  const addable = p.tools.filter((tl) => tl.hasData && !platformToolAttached(tl.id, tools, sources) && (tl.kind !== 'order_create' || p.aiOrderAuthorized))
  const addAll = () => {
    let next: AiBotToolsJson = { ...(tools ?? {}) }
    for (const tl of addable) next = setPlatformTool(tl.id, true, next, sources)
    onTools(next)
    toast.success(t('tools.platform.addedAll', { count: addable.length, name: p.name }))
  }

  return (
    <div className={cn('rounded-lg border', p.comingSoon && 'opacity-60')}>
      <div className="flex items-center gap-3 px-3 py-2.5">
        <PlatformIcon id={p.id} className="h-6 w-6" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{p.name}</p>
          {!p.comingSoon && <p className="text-[11px] text-muted-foreground">
            {!p.connected ? t('tools.platform.notConnected') : !p.enabled ? t('tools.platform.paused') : !p.aiAuthorized ? t('tools.platform.needAuth') : t('tools.platform.direct')}
          </p>}
        </div>
        {state}
        {ready && addable.length > 0 && <Button size="sm" variant="ghost" onClick={addAll} disabled={!canEdit}>{t('tools.platform.addAll')}</Button>}
      </div>
      {p.tools.length > 0 && (
        <ul className="divide-y border-t">
          {p.tools.map((tl) => {
            const on = platformToolAttached(tl.id, tools, sources)
            return (
              <li key={tl.id} className="flex items-center gap-3 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm">{tl.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{tl.description}{!tl.hasData && ready ? t('tools.platform.noSyncedData') : ''}</p>
                </div>
                {on ? (
                  <Button size="sm" variant="ghost" className="text-success" disabled={!canEdit} onClick={() => onTools(setPlatformTool(tl.id, false, tools, sources))} title={t('tools.platform.clickRemove')}><Check className="h-3.5 w-3.5" /> {t('tools.platform.added')}</Button>
                ) : tl.kind === 'order_create' && ready && !p.aiOrderAuthorized ? (
                  <Button size="sm" onClick={onAuthorizeOrder} disabled={!canEdit}><ShieldCheck className="h-3.5 w-3.5" /> {t('tools.orderCreate.authorize')}</Button>
                ) : (
                  <Button size="sm" variant="outline" disabled={!ready || !canEdit || !tl.hasData || (tl.kind === 'order_create' && !p.aiOrderAuthorized)}
                    title={!ready ? t('tools.platform.connectFirst') : !tl.hasData ? t('tools.platform.noDataTool') : undefined}
                    onClick={() => { onTools(setPlatformTool(tl.id, true, tools, sources)); toast.success(t('tools.platform.addedOne', { name: tl.name })) }}>
                    <Plus className="h-3.5 w-3.5" /> {t('common:actions.add')}
                  </Button>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

const evotechScopes = (): string[] => [
  i18n.t('aiStaff:tools.authorize.scopeProducts'),
  i18n.t('aiStaff:tools.authorize.scopeStock'),
  i18n.t('aiStaff:tools.authorize.scopeOrders'),
  i18n.t('aiStaff:tools.authorize.scopeMember'),
]

function AuthorizeDialog({ p, onClose }: { p: PlatformEntry; onClose: () => void }) {
  const { t } = useTranslation('aiStaff')
  const auth = useAuthorizeEvotechAi()
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> {t('tools.authorize.title', { name: p.name })}</DialogTitle>
          <DialogDescription>{t('tools.authorize.description', { name: p.name })}</DialogDescription>
        </DialogHeader>
        <ul className="space-y-1.5 rounded-lg border bg-muted/30 p-3 text-sm">
          {evotechScopes().map((s) => <li key={s} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />{s}</li>)}
        </ul>
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Globe className="h-3.5 w-3.5" /> {t('tools.authorize.direct', { name: p.name })}</p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t('common:actions.cancel')}</Button>
          <Button disabled={auth.isPending} onClick={() => auth.mutate(true, {
            onSuccess: () => { toast.success(t('tools.authorize.done', { name: p.name })); onClose() },
            onError: (e) => toast.error(apiError(e)),
          })}>
            {auth.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} {t('tools.authorize.confirm')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** Uỷ quyền RIÊNG cho AI tạo đơn nháp — mở rộng thêm trên uỷ quyền đọc dữ liệu, không thay thế. */
function AuthorizeOrderDialog({ p, onClose }: { p: PlatformEntry; onClose: () => void }) {
  const { t } = useTranslation('aiStaff')
  const auth = useAuthorizeEvotechAiOrder()
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> {t('tools.orderCreate.authTitle', { name: p.name })}</DialogTitle>
          <DialogDescription>{t('tools.orderCreate.authDescription', { name: p.name })}</DialogDescription>
        </DialogHeader>
        <ul className="space-y-1.5 rounded-lg border bg-muted/30 p-3 text-sm">
          <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />{t('tools.orderCreate.authScopeDraftOnly')}</li>
          <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />{t('tools.orderCreate.authScopeCustomerConfirm')}</li>
          <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />{t('tools.orderCreate.authScopeNoVoucher')}</li>
        </ul>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t('common:actions.cancel')}</Button>
          <Button disabled={auth.isPending} onClick={() => auth.mutate(true, {
            onSuccess: () => { toast.success(t('tools.orderCreate.authDone', { name: p.name })); onClose() },
            onError: (e) => toast.error(apiError(e)),
          })}>
            {auth.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} {t('tools.authorize.confirm')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
